'use client';

import { fetchClientIp } from '@/util/fetch-client-ip';
import { isRequired, validateEmail2 } from '@/util/formValidators';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import clsx from 'clsx';
import Image from 'next/image';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import BtnPagination from '../btn-pagination';
import FormError from '../FormError';
import LoadingIndicator from '../LoadingIndicator';
import SuccessDialog from './success-dialog';

const PostSection = () => {
  const { handleSubmit, reset, register, control, formState, setError, clearErrors } = useForm({
    defaultValues: {
      firstname: '',
      email: '',
      pic: '',
      story: '',
      is_wedding_2026: false,
    },
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [sortField, setSortField] = useState('created_at');
  const listAnchorRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [successDialogState, setSuccessDialogState] = useState({
    open: false,
    isPlanning: false,
  });
  const [reqAddEngagementStory, { loading: submitLoading, error: formError, reset: resetMutation }] = useMutation(gql`
    mutation addEngagementStory($input: EngagementStoryInput) {
      addEngagementStory(input: $input) {
        code
        message
        content
      }
    }
  `);
  const [votesLoadingIds, setVotesLoadingIds] = useState<number[]>([]);
  const [responseError, setResponseError] = useState<null | Error>(null);
  const [voteCacheMap, setVoteCacheMap] = useState({});

  const [currentIp, setCurrentIp] = useState('');

  useEffect(() => {
    (async () => {
      const ip = await fetchClientIp();
      setCurrentIp(ip);
    })();
  }, []);

  const [reqAddEngagementStoryVote] = useMutation(gql`
    mutation addEngagementStoryVote($input: EngagementStoryVoteInput) {
      addEngagementStoryVote(input: $input) {
        code
        message
        content
      }
    }
  `);
  const [reqRemoveEngagementStoryVote] = useMutation(gql`
    mutation removeEngagementStoryVote($input: RemoveEngagementStoryVoteInput) {
      removeEngagementStoryVote(input: $input) {
        code
        message
        content
      }
    }
  `);

  const {
    data: getEngagementStoryRes,
    loading: getEngagementStoryInProgress,
    refetch,
  } = useQuery(
    gql`
      query getEngagementStory($pageSize: Int, $currentPage: Int, $sort: EngagementStorySortInput) {
        getEngagementStory(pageSize: $pageSize, currentPage: $currentPage, sort: $sort) {
          items {
            id
            name
            content
            pic
            votes
          }
          page_info {
            current_page
            total_pages
          }
        }
      }
    `,
    {
      variables: {
        pageSize: 6,
        currentPage,
        sort: {
          [sortField]: 'DESC',
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        const items = data?.getEngagementStory?.items || [];
        setVoteCacheMap(
          items.reduce((acc, cur) => {
            acc[cur.id] = cur.votes;
            return acc;
          }, {}),
        );
      },
    },
  );
  const items = getEngagementStoryRes?.getEngagementStory?.items || [];
  const totalPages = getEngagementStoryRes?.getEngagementStory?.page_info?.total_pages || 0;

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>, onChange: ControllerRenderProps['onChange']) => {
    const eventTarget = event.target;
    const file = eventTarget.files?.[0];

    if (!file) {
      eventTarget.value = '';
      return;
    }

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setError('pic', { type: 'custom', message: '1 image, jpg/png only.' });
      eventTarget.value = '';
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      eventTarget.value = '';
      setError('pic', { type: 'custom', message: 'Image less than 3MB.' });
      return;
    }

    clearErrors('pic');
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      onChange(reader.result);
    };
    eventTarget.value = '';
  };

  const handleVote = (entryId: number, prevVoted: boolean) => {
    if (!currentIp || votesLoadingIds.includes(entryId)) return;

    setVotesLoadingIds(x => x.concat(entryId));

    if (prevVoted) {
      reqRemoveEngagementStoryVote({
        variables: {
          input: {
            id: entryId,
            ip: currentIp,
          },
        },
      })
        .then(({ data }) => {
          if (String(data?.removeEngagementStoryVote?.code) === '200') {
            setVoteCacheMap(prev => {
              return {
                ...prev,
                [entryId]: Array.isArray(prev[entryId]) ? prev[entryId].filter(x => x !== currentIp) : [],
              };
            });
          }

          setVotesLoadingIds(prev => {
            return prev.filter(x => x !== entryId);
          });
        })
        .catch(() => {
          setVotesLoadingIds(prev => {
            return prev.filter(x => x !== entryId);
          });
        });
    } else {
      reqAddEngagementStoryVote({
        variables: {
          input: {
            pid: entryId,
            ip: currentIp,
          },
        },
      })
        .then(({ data }) => {
          if (String(data?.addEngagementStoryVote?.code) === '200') {
            setVoteCacheMap(prev => {
              return {
                ...prev,
                [entryId]: Array.isArray(prev[entryId]) ? prev[entryId].concat(currentIp) : [currentIp],
              };
            });
          }

          setVotesLoadingIds(prev => {
            return prev.filter(x => x !== entryId);
          });
        })
        .catch(() => {
          setVotesLoadingIds(prev => {
            return prev.filter(x => x !== entryId);
          });
        });
    }
  };

  const handlePageChange = (nextPage: number) => {
    listAnchorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setCurrentPage(nextPage);
  };

  const handleSubmitForm = values => {
    reqAddEngagementStory({
      variables: {
        input: { ...values, is_wedding_2026: values.is_wedding_2026 ? 1 : 0 },
      },
    }).then(({ data }) => {
      if (String(data?.addEngagementStory?.code) === '200') {
        setSuccessDialogState({ open: true, isPlanning: values.is_wedding_2026 });

        if (currentPage === 1 && sortField === 'created_at') {
          refetch();
        }

        setSortField('created_at');
        setCurrentPage(1);
        reset();
      } else {
        setResponseError(new Error(data?.addEngagementStory?.content));
      }
    });
  };

  return (
    <div className='mt-[30px] lg_mt-20'>
      <SuccessDialog
        open={successDialogState.open}
        isPlanning={successDialogState.isPlanning}
        onOpenChange={nextOpen => {
          setSuccessDialogState(x => ({ ...x, open: nextOpen }));
        }}
      />
      <form
        onSubmit={handleSubmit(handleSubmitForm)}
        onChange={() => {
          setResponseError(null);

          if (formError) {
            resetMutation();
          }
        }}
        className='mx-auto max-w-site py-5 lg_py-0'
      >
        <div className='text-center font-Shango text-[24px] leading-none lg_text-[34px] lg_leading-[1.2]'>
          I WANT TO POST
        </div>
        <div className='mt-5 px-4 lg_mt-[50px] lg_flex lg_flex-row lg_gap-x-[60px] lg_px-0'>
          <Image
            alt='i want to post'
            src={process.env.NEXT_PUBLIC_BASE_PATH + '/images/just-said-yes/form-cover.webp'}
            width={587}
            height={748}
            unoptimized
            className='hidden max-w-[587px] flex-none lg_block'
          />
          <div className='flex-1'>
            <div className='flex flex-col gap-x-[72px] gap-y-[15px] lg_flex-row'>
              <div className='flex-1'>
                <div className='text-[15px] before_mr-1 before_text-e73635 before_content-["*"] lg_text-[17px] lg_leading-[1.4]'>
                  First Name
                </div>
                <input
                  {...register('firstname', { validate: isRequired })}
                  className='mt-1.5 h-10 w-full border-1 border-solid border-E7DED5 px-2.5 outline-0'
                />
                <div className='relative'>
                  <div className='absolute left-0 top-0 text-xs text-e73635'>
                    <ErrorMessage
                      errors={formState.errors}
                      name='firstname'
                    ></ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex-1'>
                <div className='text-[15px] before_mr-1 before_text-e73635 before_content-["*"] lg_text-[17px] lg_leading-[1.4]'>
                  Email
                </div>
                <input
                  {...register('email', { validate: validateEmail2 })}
                  className='mt-1.5 h-10 w-full border-1 border-solid border-E7DED5 px-2.5 outline-0'
                />
                <div className='relative'>
                  <div className='absolute left-0 top-0 text-xs text-e73635'>
                    <ErrorMessage
                      errors={formState.errors}
                      name='email'
                    ></ErrorMessage>
                  </div>
                </div>
              </div>
            </div>

            <div className='mt-5 lg_mt-[30px]'>
              <div className='text-[15px] leading-5 lg_text-[17px] lg_leading-[1.4]'>Image</div>
              <Controller
                control={control}
                name='pic'
                render={({ field: { onChange, value } }) => {
                  return (
                    <>
                      {value ? (
                        <div className='relative mt-2 h-20 w-20 border-1 border-solid border-E7DED5'>
                          <img
                            src={value}
                            className='h-full w-full object-contain'
                            alt=''
                          />
                          <button
                            type='button'
                            onClick={() => {
                              onChange('');
                            }}
                            className='absolute -right-2 -top-2 z-[2] rounded-[50%] shadow-md'
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='16'
                              height='16'
                              fill='none'
                              viewBox='0 0 16 16'
                            >
                              <g clipPath='url(#clip0_20827_49062)'>
                                <path
                                  fill='#80583F'
                                  d='M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8m3.36 10.8c.159.16.159.4 0 .56-.16.159-.4.159-.56 0L8 8.56l-2.8 2.8c-.16.159-.4.159-.56 0a.387.387 0 0 1 0-.56L7.44 8l-2.8-2.8a.387.387 0 0 1 0-.56c.16-.159.4-.159.56 0L8 7.44l2.8-2.8c.16-.159.4-.159.56 0 .16.16.16.4 0 .56L8.56 8z'
                                ></path>
                              </g>
                              <defs>
                                <clipPath id='clip0_20827_49062'>
                                  <path
                                    fill='#fff'
                                    d='M0 0h16v16H0z'
                                  ></path>
                                </clipPath>
                              </defs>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          type='button'
                          className='mt-2 flex h-20 w-20 items-center justify-center border-1 border-solid border-E7DED5'
                          onClick={() => {
                            fileInputRef.current?.click();
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='21'
                            height='22'
                            fill='none'
                            viewBox='0 0 21 22'
                          >
                            <path
                              fill='#E7DED5'
                              d='M11.048 10.286h9.524v1.429h-9.524V22H9.524V11.715H0v-1.429h9.524V0h1.524z'
                            ></path>
                          </svg>
                        </button>
                      )}

                      <input
                        className='hidden'
                        type='file'
                        accept='image/png,image/jpeg'
                        onChange={e => {
                          handleFileInputChange(e, onChange);
                        }}
                        ref={fileInputRef}
                      />
                    </>
                  );
                }}
              />
              <div className='relative'>
                <div className='absolute left-0 top-0 text-xs text-e73635'>
                  <ErrorMessage
                    errors={formState.errors}
                    name='pic'
                  ></ErrorMessage>
                </div>
              </div>
            </div>

            <div className='mt-5 lg_mt-[30px]'>
              <div className='text-[15px] before_mr-1 before_text-e73635 before_content-["*"] lg_text-[17px] lg_leading-[1.4]'>
                Text
              </div>
              <textarea
                {...register('story', { validate: isRequired })}
                className='mt-2 h-10 min-h-[45px] w-full border-1 border-solid border-E7DED5 p-3 outline-0 focus_border-80583F lg_min-h-[185px]'
              />
              <div className='relative'>
                <div className='absolute left-0 top-0 text-xs text-e73635'>
                  <ErrorMessage
                    errors={formState.errors}
                    name='story'
                  ></ErrorMessage>
                </div>
              </div>
            </div>

            <label className='mt-5 block w-max cursor-pointer leading-none'>
              <div className='relative inline-block'>
                <input
                  type='checkbox'
                  className='peer inline-block h-[22px] w-[22px] cursor-pointer appearance-none border-1 border-solid border-80583F align-bottom'
                  {...register('is_wedding_2026')}
                />
                <div className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 peer-checked_block '>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='22'
                    height='22'
                    fill='none'
                    viewBox='0 0 22 22'
                  >
                    <path
                      fill='#80583F'
                      d='M22 22H0V0h22zM8.798 13.752 5.45 10.405 4.34 11.518l3.347 3.346-.025.025 1.112 1.109 8.89-8.889L16.55 6z'
                    ></path>
                  </svg>
                </div>
              </div>
              <span className='ml-[9px] text-[15px] leading-[1.4] text-80583F lg_ml-4 lg_text-[17px]'>
                I&apos;m planning to have my wedding in 2026.
              </span>
            </label>
            <button
              type='submit'
              disabled={submitLoading}
              className='mx-auto mt-5 flex h-12 min-w-[250px] items-center  justify-center rounded-[3px] border-1 border-solid border-80583F bg-80583F px-1 text-[15px] font-medium leading-[1.4]  text-white lg_mt-[30px] lg_hover_bg-white lg_hover_text-80583F'
            >
              SUBMIT{submitLoading && '...'}
            </button>
            <FormError errors={[formError, responseError]} />
          </div>
        </div>
      </form>

      <div className='mt-[30px] bg-f8f3ef px-4 py-5 lg_mt-20 lg_px-0 lg_pb-[60px] lg_pt-10'>
        <h2 className='text-center font-Shango text-[22px] leading-none lg_text-[34px] lg_leading-[1.2]'>
          MY BEAUTIFUL ENGAGEMENT- ALL ENTRIES
        </h2>

        <div className='mx-auto mt-5 max-w-site lg_mt-[50px]'>
          <div
            className='flex  items-center gap-x-4 text-[17px] leading-[1.4] '
            ref={listAnchorRef}
          >
            <button
              className={clsx(sortField === 'likes' && 'text-BE9983')}
              type='button'
              onClick={() => {
                setCurrentPage(1);
                setSortField('likes');
              }}
            >
              Most Liked
            </button>
            <span className='h-[15px] w-[1px] flex-none bg-BE9983'></span>
            <button
              type='button'
              className={clsx(sortField === 'created_at' && 'text-BE9983')}
              onClick={() => {
                setCurrentPage(1);
                setSortField('created_at');
              }}
            >
              Latest
            </button>
          </div>

          {getEngagementStoryInProgress ? (
            <LoadingIndicator />
          ) : (
            <ul className='mt-2.5 flex flex-col  gap-y-2.5 lg_mt-[30px] lg_grid lg_grid-cols-2 lg_gap-x-5 lg_gap-y-[30px] '>
              {items.map(x => {
                if (!x) return null;

                return (
                  <EntryCard
                    key={x.id}
                    onToggleVote={handleVote}
                    entry={x}
                    currentIp={currentIp || ''}
                    isVoteLoading={votesLoadingIds.includes(x.id)}
                    votes={voteCacheMap[x.id] || []}
                  />
                );
              })}
            </ul>
          )}

          <BtnPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={handlePageChange}
            className='mt-5 lg_mt-[50px]'
          />
        </div>
      </div>
    </div>
  );
};

const EntryCard = ({ onToggleVote, entry, currentIp, isVoteLoading, votes }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [showFullContent, setShowFullContent] = useState(false);
  const [showSeeMore, setShowSeeMore] = useState(false);
  const isVotedByCustomer = Array.isArray(votes) && votes.includes(currentIp);

  useEffect(() => {
    const contentNode = contentRef.current;

    if (!contentNode) return;

    const range = document.createRange();
    range.setStart(contentNode, 0);
    range.setEnd(contentNode, contentNode.childNodes.length);
    let rangeWidth = range.getBoundingClientRect().width;
    let rangeHeight = range.getBoundingClientRect().height;
    const offsetWidth = rangeWidth - Math.floor(rangeWidth);

    if (offsetWidth < 0.001) {
      rangeWidth = Math.floor(rangeWidth);
    }

    const offsetHeight = rangeHeight - Math.floor(rangeHeight);

    if (offsetHeight < 0.001) {
      rangeHeight = Math.floor(rangeHeight);
    }

    const { top, left, right, bottom } = getPadding(contentNode);
    const horizontalPadding = left + right;
    const verticalPadding = top + bottom;

    if (
      rangeWidth + horizontalPadding > contentNode.offsetWidth ||
      rangeHeight + verticalPadding > contentNode.offsetHeight ||
      contentNode.scrollWidth > contentNode.offsetWidth
    ) {
      setShowSeeMore(true);
    }
  }, []);

  return (
    <li className='bg-white p-[14px]  lg_p-[30px]'>
      <div className=' flex gap-x-[15px] lg_gap-x-[30px]'>
        <div className=' flex-none pb-8 lg_pb-[19px]'>
          <img
            className=' h-[200px] w-[150px] object-contain lg_h-[427px] lg_w-[320px]'
            alt=''
            src={entry.pic || process.env.NEXT_PUBLIC_BASE_PATH + '/images/just-said-yes/default-cover.webp'}
          />
        </div>
        <div className='flex flex-1 flex-col items-start '>
          <div className='flex-none text-[15px] leading-[1.4] text-80583F lg_text-[20px]'>{entry.name}</div>
          <div
            ref={contentRef}
            className={clsx(
              'mt-2  w-full flex-1 text-[13px]  leading-[1.5] lg_mt-4 lg_text-[17px]',
              showFullContent
                ? ''
                : 'overflow-hidden  [-webkit-box-orient:vertical] [-webkit-line-clamp:8] [display:-webkit-box] lg_[-webkit-line-clamp:14]',
            )}
          >
            {entry.content}
          </div>
          <button
            className={clsx(
              'mt-1 flex-none text-[13px] leading-[1.4] text-80583F underline lg_mt-2 lg_text-[15px] lg_leading-5',
              !showSeeMore && 'invisible',
            )}
            type='button'
            onClick={() => {
              setShowFullContent(x => !x);
            }}
          >
            {showFullContent ? 'See Less' : 'See More'}
          </button>
          <div className='flex min-h-[30px] items-end gap-x-2  self-end lg_min-h-[34px] lg_gap-x-2.5'>
            <button
              type='button'
              disabled={isVoteLoading}
              onClick={() => {
                if (isVoteLoading) return;

                onToggleVote(entry.id, isVotedByCustomer);
              }}
            >
              {isVotedByCustomer ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='26'
                  height='24'
                  fill='none'
                  viewBox='0 0 26 24'
                  className={clsx(isVoteLoading && 'animate-pulse', 'h-auto w-5 lg_w-[26px]')}
                >
                  <path
                    fill='#80583F'
                    d='M13 24a.57.57 0 0 1-.412-.166L8.904 20.1c-.048-.048-4.789-4.841-5.469-5.525-2-2.014-3.176-4.271-3.398-6.531-.221-2.248.552-4.386 2.175-6.02C3.804.422 5.742-.249 7.82.083 9.55.359 11.333 1.325 13 2.885 14.668 1.325 16.45.36 18.182.083c2.076-.332 4.015.34 5.606 1.94 1.624 1.635 2.396 3.773 2.175 6.02-.222 2.26-1.397 4.52-3.398 6.533-1.484 1.493-9.077 9.181-9.153 9.259A.57.57 0 0 1 13 24'
                  ></path>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='26'
                  height='24'
                  fill='none'
                  viewBox='0 0 26 24'
                  className={clsx(isVoteLoading && 'animate-pulse', 'h-auto w-5 lg_w-[26px]')}
                >
                  <path
                    stroke='#80583F'
                    strokeWidth='1.6'
                    d='M18.308.873c1.8-.287 3.486.28 4.912 1.715 1.46 1.469 2.144 3.373 1.947 5.378-.2 2.034-1.266 4.13-3.17 6.046a9541 9541 0 0 0-8.997 9.1l-3.526-3.575c-.048-.048-4.79-4.843-5.471-5.527-1.904-1.916-2.97-4.01-3.17-6.045v-.001c-.197-2.005.488-3.909 1.947-5.377v-.001C4.204 1.152 5.89.586 7.691.873c1.533.245 3.175 1.112 4.761 2.597l.547.51.547-.51c1.586-1.485 3.228-2.352 4.76-2.597Z'
                  ></path>
                </svg>
              )}
            </button>
            <span className='text-[15px] leading-[1.4] text-80583F lg_text-[17px]'>
              {Array.isArray(votes) ? votes.length : 0}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
};

const getPadding = (el: HTMLElement) => {
  const style = window.getComputedStyle(el, null);
  const paddingLeft = Number.parseInt(style.paddingLeft, 10) || 0;
  const paddingRight = Number.parseInt(style.paddingRight, 10) || 0;
  const paddingTop = Number.parseInt(style.paddingTop, 10) || 0;
  const paddingBottom = Number.parseInt(style.paddingBottom, 10) || 0;
  return {
    left: paddingLeft,
    right: paddingRight,
    top: paddingTop,
    bottom: paddingBottom,
  };
};

export default PostSection;
